import { useEffect, useState } from "react";
import { CreditCard, CheckCircle2, XCircle, Loader2, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const Row = ({ ok, label, detail }) => (
  <div className="flex items-center gap-2 text-sm">
    {ok ? <CheckCircle2 className="h-4 w-4 text-[#4a8a56]" /> : <XCircle className="h-4 w-4 text-red-500" />}
    <span>{label}</span>{detail && <span className="text-gray-400">· {detail}</span>}
  </div>
);

export function AdminStripeCard() {
  const [status, setStatus] = useState(null);
  const [sk, setSk] = useState("");
  const [wh, setWh] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try { setStatus((await api.get("/admin/stripe")).data); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!sk && !wh) { toast.error("Inserisci almeno una chiave"); return; }
    setSaving(true);
    try {
      const body = {};
      if (sk) body.secret_key = sk;
      if (wh) body.webhook_secret = wh;
      const { data } = await api.put("/admin/stripe", body);
      setStatus(data); setSk(""); setWh("");
      toast.success(data.account_ok ? "Stripe collegato correttamente!" : "Salvato, ma Stripe segnala un problema");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const pricesOk = status?.prices && Object.values(status.prices).every(Boolean);
  return (
    <div className="rounded-2xl mo-card p-6" data-testid="admin-stripe-card">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-xl bg-[#5aa469]/12 flex items-center justify-center"><CreditCard className="h-5 w-5 text-[#4a8a56]" /></div>
          <div>
            <h2 className="font-display font-semibold text-xl">Collegamento Stripe</h2>
            <p className="text-sm text-gray-500">Chiavi usate per abbonamenti, servizi e incassi. Sovrascrivono quelle del deployment.</p>
          </div>
        </div>
        <Button variant="ghost" size="sm" onClick={load} className="text-gray-500" data-testid="admin-stripe-refresh"><RefreshCw className="h-4 w-4 mr-1" />Ricontrolla</Button>
      </div>

      {!status ? <div className="py-6 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-[#5aa469]" /></div> : (
        <div className="mt-5 grid md:grid-cols-2 gap-6">
          <div className="space-y-2" data-testid="admin-stripe-status">
            <Row ok={status.mode === "live"} label={`Modalità: ${status.mode === "live" ? "LIVE" : status.mode === "test" ? "TEST" : "chiave mancante"}`} detail={status.key_hint ? `chiave …${status.key_hint} (${status.key_source === "admin" ? "da pannello" : "da deployment"})` : ""} />
            <Row ok={status.account_ok} label={status.account_ok ? `Account Stripe valido (${status.country})` : "Account non raggiungibile"} detail={status.error} />
            <Row ok={!!status.charges_enabled} label={status.charges_enabled ? "Abilitato a incassare" : "Non abilitato a incassare"} />
            <Row ok={!!pricesOk} label={pricesOk ? "Prezzi dei piani presenti" : "Prezzi dei piani mancanti (si creano al primo acquisto)"} />
            <Row ok={status.webhook_secret_set} label={status.webhook_secret_set ? "Webhook configurato" : "Webhook non configurato"} />
          </div>
          <div className="space-y-3">
            <div>
              <Label>Nuova chiave segreta (sk_live_… / rk_live_…)</Label>
              <Input type="password" value={sk} onChange={(e) => setSk(e.target.value)} placeholder="sk_live_…" className="mt-1.5 h-11 rounded-lg font-mono text-sm" data-testid="admin-stripe-sk" />
            </div>
            <div>
              <Label>Signing secret webhook (whsec_…)</Label>
              <Input type="password" value={wh} onChange={(e) => setWh(e.target.value)} placeholder="whsec_…" className="mt-1.5 h-11 rounded-lg font-mono text-sm" data-testid="admin-stripe-wh" />
            </div>
            <Button onClick={save} disabled={saving} className="rounded-full bg-[#5aa469] hover:bg-[#4a8a56] text-white font-semibold" data-testid="admin-stripe-save">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Salva e verifica"}
            </Button>
            <p className="text-xs text-gray-400">Le chiavi vengono verificate con Stripe prima di essere salvate e conservate cifrate.</p>
          </div>
        </div>
      )}
    </div>
  );
}
